import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction, type RefObject } from 'react'
import { createReportRequestGuard, shouldRefreshReport, type ReportRequestGuard } from '../lib/report_freshness'

interface ReportState<T> { key: string | null; report: T | null; error: boolean; loading: boolean }
interface ReportSession<T> {
  key: string | null; load: () => Promise<T>; guard: ReportRequestGuard; inFlight: boolean; lastStartedAt: number
}
interface VerifiedReport<T> { report: T | null; error: boolean; loading: boolean; refresh: () => void }
type Setter<T> = Dispatch<SetStateAction<ReportState<T>>>

function request<T>(session: ReportSession<T>, setState: Setter<T>, force: boolean): void {
  const key = session.key
  if (!key || session.inFlight || (!force && !shouldRefreshReport(session.lastStartedAt, Date.now(), false, document.visibilityState === 'visible'))) return
  const token = session.guard.begin(key)
  session.inFlight = true
  session.lastStartedAt = Date.now()
  setState(previous => ({ key, report: previous.key === key ? previous.report : null, error: false, loading: true }))
  void session.load().then(report => {
    if (session.guard.accepts(token, session.key)) setState({ key, report, error: false, loading: false })
  }).catch(() => {
    if (session.guard.accepts(token, session.key)) setState({ key, report: null, error: true, loading: false })
  }).finally(() => {
    if (session.guard.accepts(token, session.key)) session.inFlight = false
  })
}

function useSubscriptions<T>(key: string | null, session: RefObject<ReportSession<T>>, refresh: (force?: boolean) => void, setState: Setter<T>): void {
  useEffect(() => {
    session.current.guard.invalidate(key)
    session.current.inFlight = false
    session.current.lastStartedAt = 0
    setState({ key, report: null, error: false, loading: false })
    if (key) refresh(true)
    const focus = () => refresh()
    const visibility = () => { if (document.visibilityState === 'visible') refresh() }
    window.addEventListener('focus', focus)
    document.addEventListener('visibilitychange', visibility)
    const timer = window.setInterval(() => refresh(), 120_000)
    return () => {
      session.current.guard.invalidate(null)
      window.removeEventListener('focus', focus)
      document.removeEventListener('visibilitychange', visibility)
      window.clearInterval(timer)
    }
  }, [key, refresh, session, setState])
}

export function useVerifiedReport<T>(viewingKey: string | null, load: () => Promise<T>): VerifiedReport<T> {
  const session = useRef<ReportSession<T>>({ key: viewingKey, load, guard: createReportRequestGuard(), inFlight: false, lastStartedAt: 0 })
  session.current.key = viewingKey
  session.current.load = load
  const [state, setState] = useState<ReportState<T>>({ key: null, report: null, error: false, loading: false })
  const refresh = useCallback((force = false) => request(session.current, setState, force), [])
  useSubscriptions(viewingKey, session, refresh, setState)
  const current = state.key === viewingKey ? state : { report: null, error: false, loading: !!viewingKey }
  return { report: current.report, error: current.error, loading: current.loading, refresh: () => refresh(true) }
}
